import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";

/**
 * Retroactively mark a Family/Sample/Personal movement as paid.
 *
 * Props:
 *   row      — the movement_log row (must be transfer_type='sample' & !paid)
 *   onClose  — close without action
 *   onPaid(result) — success callback (parent refreshes movement list + revenue)
 */
export default function MarkSamplePaidDialog({ row, onClose, onPaid }) {
  const { t } = useTranslation();
  const qty = Number(row?.quantity || 0);
  const wholesale = Number(row?.wholesale_price || 0);
  // v407.0: OWED rows carry their exact amount — prefill it over wholesale.
  const owedAmt =
    row?.owed && row?.owed_amount != null
      ? Number(row.owed_amount)
      : row?.owed && row?.sale_price != null
        ? Number(row.sale_price) * qty
        : null;
  const defaultAmount =
    owedAmt != null ? owedAmt.toFixed(2) : wholesale > 0 ? (wholesale * qty).toFixed(2) : "";
  const [amount, setAmount] = useState(defaultAmount);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!row) return null;

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error(t("mark_sample_paid.amount_required"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/movement-log/${row.id}/mark-paid`, {
        amount_paid: amt,
        note: note.trim() || undefined,
      });
      toast.success(
        t("mark_sample_paid.toast_success", {
          amount: Number(data.amount_paid).toFixed(2),
          product: data.product_name || row.product_name,
        })
      );
      onPaid?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="mark-sample-paid-dialog"
      >
        <h2 className="text-xl font-semibold tracking-tight">{t("mark_sample_paid.title")}</h2>
        <p className="text-sm text-zinc-600 mt-1">
          {t("mark_sample_paid.subtitle", {
            qty,
            unit: row.unit || t("common.units"),
            product: row.product_name,
            recipient: row.to_user_name || row.recipient_name || "—",
          })}
        </p>

        <form onSubmit={submit} className="space-y-3 mt-4">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("mark_sample_paid.field_amount")}</label>
            <BrutalInput
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="mark-paid-amount-input"
              autoFocus
            />
            {wholesale > 0 && (
              <p className="text-[11px] text-zinc-500 mt-1">
                {t("mark_sample_paid.amount_hint_wholesale", {
                  wholesale: wholesale.toFixed(2),
                  total: defaultAmount,
                })}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("mark_sample_paid.field_note")}</label>
            <textarea
              className="input-brutal h-20 resize-none"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("mark_sample_paid.note_placeholder")}
              maxLength={500}
              data-testid="mark-paid-note-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">{t("mark_sample_paid.note_hint")}</p>
          </div>
          <div className="flex gap-3 pt-1">
            <BrutalButton
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={onClose}
              disabled={busy}
              data-testid="mark-paid-cancel"
            >
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              type="submit"
              className="flex-1 !bg-emerald-600 hover:!bg-emerald-700 !border-emerald-700 text-white"
              disabled={busy}
              data-testid="mark-paid-confirm"
            >
              {busy ? t("mark_sample_paid.saving") : t("mark_sample_paid.confirm")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}
